import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { userAPI, caisseAPI } from '../../services/api';
import Rows from '../../components/ui/Rows';
import UserFilters from './UserFilters';
import CreationModal from '../../components/ui/CreationModal';
import UserEditModal from './UserEditModal';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import CustomSnackbar from '../../components/ui/Snackbar';
import editIcon from '../../assets/icons/edit.svg';
import trashIcon from '../../assets/icons/trash.svg';
import CircularIconButton from '../../components/ui/CircularIconButton';
import StatusPill from '../../components/ui/StatusPill';
import plusIcon from '../../assets/icons/plus.svg';
import chevronDownIcon from '../../assets/icons/chevron-down.svg';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import checkIcon from '../../assets/icons/check.svg';
import { getRoleLabel, formatCaissesDisplay } from '../../utils/roleUtils';

const Users = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]); // Display data
  const [caisses, setCaisses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    caisseId: '',
    active: ''
  });
  const [createModal, setCreateModal] = useState({ open: false, errors: {} });
  const [editModal, setEditModal] = useState({ open: false, userData: null, errors: {} });
  
  const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null, userFullName: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isStuck, setIsStuck] = useState(false);

  const STICKY_TOP_OFFSET = 76;

  const contentContainerSx = {
    width: '100%',
    margin: '0 auto',
    paddingLeft: { xs: '26px', sm: '42px' },
    paddingRight: { xs: '26px', sm: '42px' }
  };

  const stickyOuterSx = { ...contentContainerSx };
  
  // Sorting state
  const [sortBy, setSortBy] = useState('firstName');
  const [sortDirection, setSortDirection] = useState('ASC');

  // Fetch users with backend pagination (like Transactions)
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        size: pagination.size,
        sortBy: sortBy,
        sortDirection: sortDirection,
        ...(filters.search && { searchTerm: filters.search }),
        ...(filters.role && { role: filters.role }),
        ...(filters.caisseId && { caisseId: filters.caisseId }),
        ...(filters.active && { active: filters.active === 'true' })
      };
      
      const response = await userAPI.getAll(params);
      
      // Backend handles all filtering and pagination
      setUsers(response.data.content || []);
      setPagination(prev => ({ 
        ...prev, 
        total: response.data.totalElements || 0 
      }));
      
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Échec du chargement des utilisateurs');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.size, sortBy, sortDirection]);

  // Load users when filters or pagination change (like Transactions)
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Load caisses for caisse dropdown
  useEffect(() => {
    const loadCaisses = async () => {
      try {
        const response = await caisseAPI.getForFilters();
        const caisses = Array.isArray(response.data) ? response.data : [];
        setCaisses(caisses);
      } catch (error) {
        console.error('Error loading caisses:', error);
      }
    };
    loadCaisses();
  }, []);

  // Sorting handler
  const handleSort = (field, direction) => {
    setSortBy(field);
    setSortDirection(direction);
    // Reset to first page when sorting changes
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  // Field configuration for creation modal
  const getUserFields = (formData) => {
    const baseFields = [
      {
        name: 'firstName',
        type: 'text',
        placeholder: 'Prénom',
        required: true
      },
      {
        name: 'lastName',
        type: 'text',
        placeholder: 'Nom',
        required: true
      },
      {
        name: 'username',
        type: 'text',
        placeholder: 'Nom d\'utilisateur',
        required: true
      },
      {
        name: 'password',
        type: 'password',
        placeholder: 'Mot de passe',
        required: true
      },
      {
        name: 'role',
        type: 'select',
        placeholder: 'Rôle',
        required: true,
        options: [
          { value: 'MANAGER', label: 'Manager' },
          { value: 'AGENT', label: 'Agent de Caisse' },
          { value: 'VERIFICATION', label: 'Agent de Pointage' }
        ]
      }
    ];

    // Add caisse field based on role (matches original logic)
    const selectedRole = formData.role?.value || formData.role;
    if (selectedRole === 'AGENT') {
      baseFields.push({
        name: 'caisseIds',
        type: 'select',
        placeholder: 'Caisse',
        required: true,
        options: caisses.map(caisse => ({ value: caisse.id, label: caisse.name }))
      });
    } else if (selectedRole === 'VERIFICATION') {
      baseFields.push({
        name: 'caisseIds',
        type: 'multiselect',
        placeholder: 'Caisses',
        required: true,
        options: caisses.map(caisse => ({ value: caisse.id, label: caisse.name }))
      });
    }

    return baseFields;
  };

  // Handle filter changes (like Transactions)
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 0 })); // Reset to first page
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      role: '',
      caisseId: '',
      active: ''
    });
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleCreateUser = () => {
    setCreateModal({ open: true, errors: {} });
  };

  const handleSubmitCreateUser = async (formData) => {
    try {
      setActionLoading(true);
      
      // Transform the data to match backend expectations
      const transformedData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        password: formData.password,
        role: formData.role?.value || formData.role, // Extract value from react-select object
        caisseIds: formData.caisseIds 
          ? (Array.isArray(formData.caisseIds) 
              ? formData.caisseIds.map(item => item.value || item)
              : [formData.caisseIds.value || formData.caisseIds])
          : [], // Extract values from react-select objects
        active: formData.active
      };
      
      await userAPI.create(transformedData);
      setCreateModal({ open: false, errors: {} });
      setSnackbar({ open: true, message: 'Utilisateur créé avec succès', severity: 'success' });
      // Refresh current page data
      fetchUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      console.error('Error details:', err.response?.data);
      
      // Handle specific error cases
      let errorMessage = 'Erreur lors de la création de l\'utilisateur';
      
      if (err.response?.status === 400) {
        const errorData = err.response.data;
        if (typeof errorData === 'string') {
          // Handle string error messages from backend
          if (errorData.includes('username') || errorData.includes('nom d\'utilisateur')) {
            errorMessage = 'Un utilisateur avec ce nom d\'utilisateur existe déjà';
            // Set field-specific error
            setCreateModal(prev => ({ 
              ...prev, 
              errors: { ...prev.errors, username: 'Ce nom d\'utilisateur est déjà utilisé' }
            }));
          } else {
            errorMessage = errorData;
          }
        } else if (errorData && typeof errorData === 'object') {
          // Handle object error responses
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        }
      } else if (err.response?.status === 409) {
        errorMessage = 'Un utilisateur avec ce nom d\'utilisateur existe déjà';
        // Set field-specific error
        setCreateModal(prev => ({ 
          ...prev, 
          errors: { ...prev.errors, username: 'Ce nom d\'utilisateur est déjà utilisé' }
        }));
      } else if (err.response?.status === 500) {
        errorMessage = 'Erreur interne du serveur. Veuillez réessayer.';
      }
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditUser = (row) => {
    // Find the raw user data from the users array
    const rawUser = users.find(user => user.id === row.id);
    setEditModal({ open: true, userData: rawUser, errors: {} });
  };

  const handleSubmitEditUser = async (formData) => {
    try {
      setActionLoading(true);
      
      // Transform the data to match backend expectations
      const transformedData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        role: formData.role?.value || formData.role,
        caisseIds: formData.caisseIds 
          ? (Array.isArray(formData.caisseIds) 
              ? formData.caisseIds.map(item => item.value || item)
              : [formData.caisseIds.value || formData.caisseIds])
          : [],
        active: formData.active
      };
      
      // Add password only if it was provided (reset password was clicked)
      if (formData.password) {
        transformedData.password = formData.password;
      }
      
      await userAPI.update(editModal.userData.id, transformedData);
      setEditModal({ open: false, userData: null, errors: {} });
      setSnackbar({ open: true, message: 'Utilisateur modifié avec succès', severity: 'success' });
      // Refresh current page data
      fetchUsers();
    } catch (err) {
      console.error('Error updating user:', err);
      console.error('Error details:', err.response?.data);
      
      // Handle specific error cases
      let errorMessage = 'Erreur lors de la modification de l\'utilisateur';
      
      if (err.response?.status === 400) {
        const errorData = err.response.data;
        if (typeof errorData === 'string') {
          if (errorData.includes('username') || errorData.includes('nom d\'utilisateur')) {
            errorMessage = 'Un utilisateur avec ce nom d\'utilisateur existe déjà';
          } else {
            errorMessage = errorData;
          }
        } else if (errorData && typeof errorData === 'object') {
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        }
      } else if (err.response?.status === 409) {
        errorMessage = 'Un utilisateur avec ce nom d\'utilisateur existe déjà';
      } else if (err.response?.status === 500) {
        errorMessage = 'Erreur interne du serveur. Veuillez réessayer.';
      }
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = (userId, userFullName) => {
    setDeleteDialog({ open: true, userId, userFullName });
  };

  const confirmDeleteUser = async () => {
    try {
      setActionLoading(true);
      await userAPI.delete(deleteDialog.userId);
      setDeleteDialog({ open: false, userId: null, userFullName: '' });
      setSnackbar({ open: true, message: 'Utilisateur supprimé avec succès', severity: 'success' });
      // Refresh current page data
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la suppression de l\'utilisateur', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };


  // Check if any filters are active
  const hasActiveFilters = () => {
    return filters.search?.trim() || 
           filters.role || 
           filters.active || 
           filters.caisseId;
  };

  // Simple scroll listener for background color change
  useEffect(() => {
    const handleScroll = () => {
      setIsStuck(window.scrollY > STICKY_TOP_OFFSET);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Transform users data for the table
  const transformedUsers = users.map(user => ({
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
      role: getRoleLabel(user.role),
      caisse: formatCaissesDisplay(user.assignedCaisses, user.role),
      status: user.active ? 'Actif' : 'Inactif',
      active: user.active,
      hasActiveSession: user.hasActiveSession || false // Add active session indicator
  }));

  // Define table columns
  const columns = [
    { key: 'firstName', label: 'Prénom', width: '15%', sortable: true },
    { key: 'lastName', label: 'Nom', width: '15%', sortable: true },
    { key: 'username', label: 'Nom d\'utilisateur', width: '12%', sortable: true },
    { key: 'role', label: 'Rôle', width: '15%', sortable: true },
    { 
      key: 'caisse', 
      label: 'Caisse', 
      width: '22%',
      sortable: false, // Complex render logic, not sortable
      render: (value) => (
        <div style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'normal',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          maxWidth: '100%',
          lineHeight: '1.2'
        }}>
          {value}
        </div>
      )
    },
    { 
      key: 'active', 
      label: 'Statut', 
      width: '8%', 
      sortable: true,
      render: (value) => {
        const isActive = value === true; // value is now boolean
        const text = isActive ? 'Actif' : 'Inactif';
        return (
          <StatusPill
            text={text}
            color={isActive ? '#0AD69F' : '#EF456E'}
          />
        );
      }
    },
    { 
      key: 'actions', 
      label: 'Actions', 
      width: '3%', 
      sortable: false,
      render: (value, row) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <CircularIconButton
            icon={editIcon}
            alt="Edit"
            aria-label="edit"
            onClick={(e) => {
              e.stopPropagation();
              handleEditUser(row);
            }}
          />
          <CircularIconButton
            icon={trashIcon}
            alt="Delete"
            aria-label="delete"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteUser(row.id, `${row.firstName} ${row.lastName}`);
            }}
          />
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Sticky Header Section */}
      <Box
        className="users-sticky-header"
        sx={{
          position: 'sticky',
          top: '0px',
          zIndex: 1000,
          backgroundColor: isStuck ? '#F8F8F8' : 'transparent',
          borderBottom: isStuck ? '1px solid #E5E8EF' : 'none',
          transition: 'background-color 0.2s ease, border-bottom 0.2s ease'
        }}
      >
        <Box sx={stickyOuterSx}>
          <Box sx={{ paddingTop: '24px', paddingBottom: '8px' }}>
            {/* Page Title & Create Button */}
            <Box sx={{ marginBottom: '8px', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h1 
                style={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '28px',
                  lineHeight: '32px',
                  letterSpacing: '0px',
                  color: '#38485C',
                  opacity: 0.88,
                  textTransform: 'none',
                  margin: 0
                }}
              >
                Utilisateurs
              </h1>

              {user?.role === 'MANAGER' && (
                <Button
                  variant="contained"
                  startIcon={<img src={plusIcon} alt="plus" style={{ width: '16px', height: '16px' }} />}
                  onClick={handleCreateUser}
                  sx={{
                    height: '40px',
                    backgroundColor: '#027BC6',
                    boxShadow: '0px 20px 80px #00000011',
                    borderRadius: '12px',
                    opacity: 1,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: '#025a9c',
                    },
                  }}
                >
                  Créer un utilisateur
                </Button>
              )}
            </Box>

            {/* Filters */}
            <Box sx={{ width: '100%', overflow: 'visible', position: 'relative', zIndex: 1001 }}>
              <UserFilters
                filters={filters}
                caisses={caisses}
                onFiltersChange={handleFiltersChange}
                onReset={handleResetFilters}
                hasActiveFilters={hasActiveFilters()}
                pagination={pagination}
                setPagination={setPagination}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={contentContainerSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: '16px',
              paddingBottom: '8px',
              paddingLeft: '16px',
              paddingRight: '16px',
              color: '#919191',
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: 'Montserrat'
            }}
          >
            {columns.map((column, index) => {
              const isActionsColumn = column.key === 'actions';
              const isSortable = column.sortable && handleSort;
              const isCurrentlySorted = sortBy === column.key;
              
              return (
                <Box
                  key={index}
                  sx={{
                    paddingLeft: '24px',
                    paddingRight: '24px',
                    textAlign: isActionsColumn ? 'center' : 'left',
                    ...(isActionsColumn 
                      ? { display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }
                      : column.width
                        ? { flex: 'none', width: column.width }
                        : { flex: 1, minWidth: '150px' }),
                    ...(isActionsColumn && column.width ? { width: column.width, flexShrink: 0, flexGrow: 0 } : {}),
                    ...(column.width && !isActionsColumn ? { width: column.width } : {})
                  }}
                >
                  {isSortable ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer',
                        '&:hover': { color: '#027BC6' },
                        transition: 'color 0.2s',
                        color: isCurrentlySorted ? '#027BC6' : '#919191'
                      }}
                      onClick={() => handleSort(column.key, sortBy === column.key && sortDirection === 'ASC' ? 'DESC' : 'ASC')}
                    >
                      <span>{column.label}</span>
                      {isCurrentlySorted && (
                        <img
                          src={sortDirection === 'ASC' ? chevronUpIcon : chevronDownIcon}
                          alt={sortDirection === 'ASC' ? 'ascending' : 'descending'}
                          style={{
                            width: '12px',
                            height: '12px',
                            filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(188deg) brightness(89%) contrast(101%)'
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    column.label
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box sx={contentContainerSx}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Users Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Rows
            columns={columns}
            data={transformedUsers}
            pagination={{
              page: pagination.page,
              size: pagination.size,
              total: pagination.total,
              onPageChange: (newPage) => setPagination(prev => ({ ...prev, page: newPage })),
              onSizeChange: (newSize) => setPagination(prev => ({ ...prev, size: newSize, page: 0 }))
            }}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            title={null}
            wrapLayout={false}
            expandable={false}
            emptyMessage={hasActiveFilters() ? 'Aucun utilisateur trouvé avec ces critères' : 'Aucun utilisateur trouvé'}
            hideHeader={true}
            containerPadding={0}
          />
        )}
      </Box>

      {/* Create User Modal */}
      <CreationModal
        open={createModal.open}
        onClose={() => setCreateModal({ open: false, errors: {} })}
        onSubmit={handleSubmitCreateUser}
        title="Créer un nouvel utilisateur"
        fields={getUserFields}
        submitButtonText="Créer un nouvel utilisateur"
        loading={actionLoading}
        toggleConfig={{
          field: 'active',
          activeLabel: 'Actif',
          inactiveLabel: 'Inactif'
        }}
        externalErrors={createModal.errors}
        initialData={useMemo(() => ({ 
          firstName: '', 
          lastName: '', 
          username: '', 
          password: '', 
          role: '', 
          caisseIds: null, 
          active: true 
        }), [])}
        onFieldChange={(fieldName, value, formData) => {
          // Handle role change logic (matches original)
          if (fieldName === 'role') {
            const newRole = value?.value || value;
            if (newRole === 'AGENT') {
              formData.caisseIds = null; // Single select
            } else if (newRole === 'VERIFICATION') {
              formData.caisseIds = []; // Multi select
            } else {
              formData.caisseIds = null; // No caisse assignment for MANAGER
            }
          }
          // Return the modified formData to update the state
          return formData;
        }}
      />

      {/* Edit User Modal */}
      <UserEditModal
        open={editModal.open}
        onClose={() => setEditModal({ open: false, userData: null, errors: {} })}
        onSubmit={handleSubmitEditUser}
        loading={actionLoading}
        userData={editModal.userData}
        errors={editModal.errors}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, userId: null, userFullName: '' })}
        onConfirm={confirmDeleteUser}
        type="delete"
        title="Supprimer l'utilisateur"
        messageComponent={
          <Typography
            sx={{
              textAlign: 'center',
              fontFamily: 'Montserrat',
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: '32px',
              letterSpacing: '0px',
              opacity: 1,
              color: '#333333'
            }}
          >
            Êtes-vous sûr de vouloir supprimer l'utilisateur{' '}
            <span style={{ color: '#027BC6' }}>{deleteDialog.userFullName}</span>
            {' '}? Cette action est irréversible.
          </Typography>
        }
        confirmText="Supprimer"
        cancelText="Annuler"
        loading={actionLoading}
      />

      {/* Snackbar */}
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default Users;

